/**
 * @file frontend/src/lib/api.js
 * @module lib
 * @component api
 *
 * Api — Shared library / API client code.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import axios from 'axios';
import { captureException } from '@sentry/react';

const _raw = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = _raw && !/^https?:\/\//i.test(_raw) ? `https://${_raw}` : _raw;

/** Absolute URL for a Django-served media file (upload downloads / inline
 * previews). Django's FileField.url returns paths like "/media/foo.pdf"
 * that resolve against the frontend origin (nginx SPA fallback -> serves
 * index.html, not the file). Prefixing with BACKEND_URL points them at
 * the actual media server. Absolute URLs (S3 etc.) are returned as-is. */
export function mediaUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = BACKEND_URL || '';
  return pathOrUrl.startsWith('/') ? `${base}${pathOrUrl}` : `${base}/${pathOrUrl}`;
}

// Endpoints whose OWN 401 must never trigger a token refresh. A 401 from these
// means "these credentials are wrong", not "this session expired" -- refreshing
// is both pointless and destructive (it clears storage and hard-redirects).
const AUTH_ENDPOINTS_NO_REFRESH = [
  '/auth/login/',
  '/auth/refresh/',
  '/auth/2fa/verify/',
  '/auth/logout/',
  '/auth/register/',
];

// The wall tablet has no employee session. A 401 here is a bad kiosk
// secret, a bad PIN, or a photo upload the tablet is not signed in for.
// Refreshing would send the browser to the login page.
export const KIOSK_ENDPOINTS_NO_REFRESH = [
  '/attendance/kiosk/',
  '/attendance/badge/punch/',
  '/attendance/punch-photos/',
];

// Shared in-flight refresh. Held at module scope so every concurrent 401 awaits
// the SAME request instead of racing (the server rotates + revokes on each
// refresh, so parallel calls revoke each other).
let refreshPromise = null;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // P1 hardening (Feb 2026): send the HttpOnly refresh cookie on /api/auth/refresh
  // and other same-origin auth endpoints. Backend issues `k12erp_refresh` cookie on login.
  withCredentials: true,
});

// Request interceptor to add auth token.
// NOTE: tokens are stored in sessionStorage (not sessionStorage) to reduce XSS
// persistence. The proper long-term fix is HttpOnly cookies set by the backend
// (the k12erp_refresh cookie is already issued; access tokens should follow).
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const requestUrl = originalRequest?.url || '';
    // Never try to "refresh" a 401 that came FROM an auth endpoint. A wrong
    // password returns 401 from /auth/login/; without this guard that 401 fell
    // into the refresh path below, failed, and hit `window.location.href =
    // '/login'` -- a full page reload that wiped the error toast, so a bad
    // password looked like the login page silently reloading itself.
    const isAuthEndpoint = AUTH_ENDPOINTS_NO_REFRESH.some((p) => requestUrl.includes(p));
    const isKioskEndpoint = KIOSK_ENDPOINTS_NO_REFRESH.some((p) => requestUrl.includes(p));

    if (
      error.response?.status === 401
      && !originalRequest._retry
      && !isAuthEndpoint
      && !isKioskEndpoint
    ) {
      originalRequest._retry = true;

      try {
        // Single-flight: N concurrent 401s (a dashboard fires many parallel
        // requests) must share ONE refresh call. The backend revokes the old
        // refresh token before issuing the new one, so parallel refreshes mean
        // the first wins and every other one gets 401 "expired or revoked" ->
        // spurious logout. That was the intermittent-logout symptom.
        if (!refreshPromise) {
          refreshPromise = axios
            .post(
              `${BACKEND_URL}/api/auth/refresh/`,
              // withCredentials sends the httpOnly cookie, which the backend
              // reads FIRST. The body is the fallback for when that cookie
              // never arrives -- it is cross-site (SPA and API are on
              // different domains), so Safari/ITP and third-party-cookie
              // blocking drop it. Sending both means whichever survives works.
              { refresh_token: sessionStorage.getItem('refresh_token') || '' },
              { withCredentials: true },
            )
            .finally(() => {
              refreshPromise = null;
            });
        }

        const response = await refreshPromise;
        const { access_token, refresh_token } = response.data;
        sessionStorage.setItem('access_token', access_token);
        // Rotation: the server issues a new refresh token each time and
        // revokes the old one. Persist it or the NEXT refresh replays a
        // revoked token and logs the user out.
        if (refresh_token) sessionStorage.setItem('refresh_token', refresh_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        // Already on /login means checkAuth triggered this refresh — don't
        // redirect again or the login page reloads forever (infinite loop).
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status >= 500) {
      const safeUrl = (error.config?.url || '')
        .replace(/\/[0-9a-f-]{8,}/gi, '/:id')
        .replace(/\/\d+/g, '/:id');
      captureException(error, {
        extra: {
          url: safeUrl,
          method: error.config?.method,
          status: error.response?.status,
        },
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Fetch every page of a list endpoint and return the flattened results array.
 *
 * Handles both response shapes this API uses:
 *   - a bare array              : returned as-is (endpoint isn't paginated)
 *   - { count, next, results }  : DRF pagination; all pages are accumulated
 *                                 by following `next` until it is null.
 *
 * The `next` link is an absolute URL whose host can differ from the configured
 * backend, so we don't fetch it directly. Instead we re-issue the same `url`
 * against this client (preserving auth + baseURL), carrying only `next`'s query
 * params. That also keeps this agnostic to page-number vs cursor schemes.
 */
export const fetchAllPages = async (url, config = {}) => {
  const first = await api.get(url, config);
  const data = first.data;
  if (Array.isArray(data)) return data;                  // unpaginated list
  if (!data || !Array.isArray(data.results)) return [];  // unexpected shape

  const results = [...data.results];
  let next = data.next;
  let guard = 0;
  while (next && guard < 100) {                           // hard stop ~2000 rows
    guard += 1;
    const params = Object.fromEntries(
      new URL(next, window.location.origin).searchParams.entries()
    );
    const res = await api.get(url, { ...config, params: { ...(config.params || {}), ...params } });
    const page = res.data;
    if (Array.isArray(page)) { results.push(...page); break; }
    if (Array.isArray(page?.results)) results.push(...page.results);
    next = page?.next;
  }
  if (guard >= 100) console.warn(`fetchAllPages: hit 100-page cap for ${url}; results may be truncated`);
  return results;
};

export default api;
